import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../reduxStore/reduxHooks";
import { getConfigData, setConfigData } from "../../reduxStore/slices/configSlice";
import { CONFIG_DATA_KEY } from "../../utility/enums/common";
import { useGetApplicationId } from "./useGetApplicationId";
import { getLocale } from "../../utility/locale-helper";
import { useLazyGetAssessmentDetailsQuery } from "../../reduxStore/api/apiSlice";
import { StatusEnum } from "@amzn/hvh-simple-hire-checklist-internal-api-common";
import { isTaskEnabledByAppConfig } from "../../utility/app-config-helper";
import { ASSESSMENT_TASK_ID } from "../../config/appConfigConstants";

export interface HdeDetailsResponse {
  assessment: {
    status: StatusEnum;
    secureUrl: string;
  };
  workAuthorization: {
    status: StatusEnum;
    secureUrl: string;
  };
  workAuthorizationSupplement: {
    status: StatusEnum;
    secureUrl: string;
  };
  backgroundCheck: {
    status: StatusEnum;
  };
}

export const useGetAssessmentDetails = () => {
  const { hdeDetails } = useAppSelector(getConfigData);
  const dispatch = useDispatch();
  const applicationId = useGetApplicationId();
  const [getAssessmentDetails] = useLazyGetAssessmentDetailsQuery();

  useEffect(() => {
    if (hdeDetails) return;

    if (applicationId && isTaskEnabledByAppConfig(ASSESSMENT_TASK_ID, applicationId)) {
      getAssessmentDetails(
        {
          applicationId,
          locale: getLocale(),
        },
        true
      )
        .unwrap()
        .then((data) => {
          dispatch(
            setConfigData({
              key: CONFIG_DATA_KEY.ASSESSMENT,
              value: data,
            })
          );
        })
        .catch((error) => {
          console.error("Failed to fetch assessment details:", error);
          dispatch(
            setConfigData({
              key: CONFIG_DATA_KEY.ASSESSMENT,
              value: {
                assessment: {
                  status: StatusEnum.ERROR,
                  secureUrl: "",
                },
                workAuthorization: {
                  status: StatusEnum.ERROR,
                  secureUrl: "",
                },
                workAuthorizationSupplement: {
                  status: StatusEnum.ERROR,
                  secureUrl: "",
                },
                backgroundCheck: {
                  status: StatusEnum.ERROR,
                },
              },
            })
          );
        });
    }
  }, [applicationId, getAssessmentDetails, hdeDetails]);

  return hdeDetails;
};
